/** DESK WIDGET WAVE 2 — payments, inbox, pipeline, quotes aging, focus timer,
 * quick links, weather, charts. Honest states, demo masking, normal tiles. */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD, SPEC, mini } from './V2DeskTiles';

const useW = (req, path, ms = 90000) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => { req('GET', path).then(setData).catch(() => {}); }, [req, path]);
  useEffect(() => { load(); const t = setInterval(load, ms); return () => clearInterval(t); }, [load, ms]);
  return data;
};
const MASKS = ['a local business', 'a Brisbane trade', 'a nearby café', 'a Bayside client'];
const maskIf = (demo, s) => demo ? MASKS[(String(s || '').length) % MASKS.length] : s;
const money = (demo, v) => demo ? '$•••' : `$${Number(v || 0).toLocaleString()}`;
const emptyP = { fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 };
const rowP = { margin: 0, flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const chip = (c) => ({ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, fontWeight: 800, color: c, border: `1px solid ${c}44`, borderRadius: 999, padding: '1px 7px', flexShrink: 0 });
const ago = (ts) => { const d = (Date.now() - new Date(ts).getTime()) / 3600000; return d < 1 ? `${Math.max(1, Math.round(d * 60))}m` : d < 24 ? `${Math.round(d)}h` : `${Math.round(d / 24)}d`; };
const NeedsPerm = ({ reason, testid }) => (
  <div data-testid={testid} style={{ textAlign: 'center', padding: '10px 6px', borderRadius: 12, border: `1px dashed ${BORDER}` }}>
    <p style={{ margin: 0, fontSize: 18 }}>🔐</p>
    <p style={{ ...emptyP, marginTop: 4, fontSize: 10, lineHeight: 1.5 }}>{reason}</p>
  </div>
);

export const PaymentsTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/payments');
  if (!d) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-payments" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <p style={{ margin: '0 0 6px', fontFamily: HEAD, fontSize: 18, fontWeight: 800, color: TEAL }}>{money(ctx.demo, d.total_recent)} <span style={{ fontSize: 10, color: SUB, fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600 }}>recent</span></p>
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {d.payments.map((p, k) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 6px', margin: '2px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
            <span style={{ color: TEAL, fontSize: 11 }}>💵</span>
            <p style={rowP}>{maskIf(ctx.demo, p.name)}</p>
            {p.leak_check && <span style={chip(VIOLET)}>booked</span>}
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: TEAL, flexShrink: 0 }}>{money(ctx.demo, p.amount)}</b>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, flexShrink: 0 }}>{ago(p.ts)}</span>
          </div>
        ))}
        {d.payments.length === 0 && <p style={emptyP}>No payments yet — they'll land here the second Stripe fires.</p>}
      </div>
    </div>
  );
};

export const InboxTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/inbox', 120000);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <NeedsPerm reason={d.reason} testid="widget-inbox-permission" />;
  return (
    <div data-testid="widget-inbox">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <p style={{ margin: 0, fontFamily: HEAD, fontSize: 22, fontWeight: 800, color: d.unread ? MAGENTA : TEAL }}>{d.unread}</p>
        <p style={{ ...emptyP, fontSize: 10.5 }}>unread</p>
        <a className="no-drag" href="https://outlook.office.com/mail/" target="_blank" rel="noreferrer" data-testid="inbox-open-mail"
          style={{ ...mini, marginLeft: 'auto', textDecoration: 'none', padding: '2px 9px', fontSize: 9.5, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>Open mail →</a>
      </div>
      {d.latest.map((m, k) => (
        <p key={k} style={{ ...rowP, margin: '3px 0', fontWeight: m.unread ? 800 : 500 }}>
          {m.unread && <span style={{ color: MAGENTA }}>● </span>}{ctx.demo ? 'Message — client' : m.subject}
          <span style={{ color: SUB, fontSize: 9.5 }}> · {ctx.demo ? '•••' : m.frm}</span>
        </p>
      ))}
    </div>
  );
};

const STAGE_META = [['new', 'New', SUB], ['contacted', 'Contacted', '#7BB0EC'], ['replied', 'Replied', TEAL], ['leak_check_booked', 'Booked', VIOLET], ['quoted', 'Quoted', AMBER], ['won', 'Won', MAGENTA]];
export const PipelineTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/pipeline');
  if (!d) return <p style={emptyP}>…</p>;
  const max = Math.max(1, ...d.stages.map((s) => s.count));
  return (
    <div data-testid="widget-pipeline">
      {STAGE_META.map(([id, name, c]) => {
        const s = d.stages.find((x) => x.stage === id) || { count: 0 };
        return (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 700, color: SUB, width: 68, flexShrink: 0, textAlign: 'right' }}>{name}</span>
            <div style={{ flex: 1, height: 12, borderRadius: 6, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
              <div style={{ width: `${(s.count / max) * 100}%`, height: '100%', borderRadius: 6, background: `linear-gradient(90deg, ${c}66, ${c})`, transition: 'width 700ms ease' }} />
            </div>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, width: 24, flexShrink: 0 }}>{s.count}</b>
            {s.value ? <span style={chip(AMBER)}>{money(ctx.demo, s.value)}</span> : null}
          </div>
        );
      })}
    </div>
  );
};

// ── Quote Pipeline — the TravelTrucks 5-stage board (FIX-02 Tile 1) ──────────
const QP_STAGE_COLOR = {
  proposal_done:     '#7BB0EC',
  col_reviewed:      '#FFB469',
  quote_sent:        '#EC7C24',
  revision_required: '#F2B755',
  quote_accepted:    '#54D197',
};

export const QuotePipelineTile = ({ ctx }) => {
  const [stages, setStages] = useState(null);
  const dragRef = useRef(null);
  const load = useCallback(() => {
    ctx.req('GET', '/api/admin/desk/widgets/quote_pipeline')
      .then((d) => setStages(d.stages || [])).catch(() => {});
  }, [ctx]);
  useEffect(() => { load(); const t = setInterval(load, 90000); return () => clearInterval(t); }, [load]);

  const move = async (quote_id, from, to) => {
    if (!quote_id || from === to) return;
    setStages((prev) => {
      const card = (prev.find((c) => c.stage === from)?.cards || []).find((c) => c.quote_id === quote_id);
      return prev.map((col) => {
        if (col.stage === from) return { ...col, cards: col.cards.filter((c) => c.quote_id !== quote_id), count: col.count - 1 };
        if (col.stage === to && card) return { ...col, cards: [card, ...col.cards], count: col.count + 1 };
        return col;
      });
    });
    try {
      await ctx.req('POST', '/api/admin/desk/widgets/quote_pipeline/move', { quote_id, to_stage: to });
      ctx.load?.();   // refresh the board so the Money ticker forecast reacts
    } catch (e) { ctx.showToast?.('Move failed: ' + (e?.message || '')); }
    load();
  };

  if (!stages) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-quote-pipeline" style={{ overflowX: 'auto', display: 'flex', gap: 10, paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
      {stages.map((col) => (
        <div key={col.stage}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const p = dragRef.current; dragRef.current = null; if (p) move(p.quote_id, p.from, col.stage); }}
          style={{ flex: '1 0 190px', minWidth: 190, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: '2px 4px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: QP_STAGE_COLOR[col.stage] || SUB, flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--ff-caps)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: SUB, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: TXT, background: 'rgba(255,255,255,0.06)', borderRadius: 999, padding: '0 7px', flexShrink: 0 }}>{col.count}</span>
          </div>
          {col.cards.map((card) => (
            <div key={card.quote_id} draggable data-testid={`qp-card-${card.quote_id}`}
              onDragStart={() => { dragRef.current = { quote_id: card.quote_id, from: col.stage }; }}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 10, padding: '9px 10px', marginBottom: 7, cursor: 'grab' }}>
              <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, fontWeight: 700, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm ? ctx.dm(card.title) : card.title}</p>
              <p style={{ margin: '2px 0 6px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm ? ctx.dm(card.customer) : card.customer}</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <b style={{ fontFamily: 'Fredoka, sans-serif', fontSize: 13.5, fontWeight: 700, color: '#EC7C24' }}>{money(ctx.demo, card.total_inc)}</b>
                <span style={chip(SUB)}>{Math.round(card.age_days)}d</span>
              </div>
            </div>
          ))}
          {!col.cards.length && <p style={{ ...emptyP, textAlign: 'center', padding: '12px 0', opacity: 0.5 }}>—</p>}
        </div>
      ))}
    </div>
  );
};

// ── Fitting-bay board — the TravelTrucks build stages (FIX-02 Tile 2) ────────
const BAY_STAGE_COLOR = {
  parts_ordered:    '#7BB0EC',
  in_bay:           '#FFB469',
  fitting:          '#EC7C24',
  qa:               '#F2B755',
  ready_for_pickup: '#54D197',
};
// Derive a build-type icon (TravelTrucks icon set at /brand/icons/) from the
// vehicle/build text — a light keyword match, defaulting to the bay icon.
const BUILD_ICON_RULES = [
  [/snorkel/,                'snorkel'],
  [/bull ?bar|nudge/,        'bullbar'],
  [/canopy/,                 'canopy'],
  [/suspension|lift|gvm/,    'suspension'],
  [/batter|lithium|dc-?dc|inverter|dual ?battery/, 'dualbattery'],
  [/winch|recovery/,         'recovery'],
  [/tyre|tire|wheel|rim/,    'tyres'],
  [/drawer/,                 'drawers'],
  [/roof|rack/,              'roofrack'],
  [/tank|water/,             'watertank'],
  [/awning/,                 'awning'],
  [/tow ?bar|hitch/,         'towbar'],
  [/light ?bar|led/,         'lightbar'],
  [/tray/,                   'uteTray'],
];
const buildIcon = (text) => {
  const t = (text || '').toLowerCase();
  const hit = BUILD_ICON_RULES.find(([re]) => re.test(t));
  return `/brand/icons/${hit ? hit[1] : 'bay'}.svg`;
};

export const FittingBayTile = ({ ctx }) => {
  const [stages, setStages] = useState(null);
  const dragRef = useRef(null);
  const load = useCallback(() => {
    ctx.req('GET', '/api/admin/desk/widgets/bays')
      .then((d) => setStages(d.stages || [])).catch(() => {});
  }, [ctx]);
  useEffect(() => { load(); const t = setInterval(load, 90000); return () => clearInterval(t); }, [load]);

  const move = async (job_id, from, to) => {
    if (!job_id || from === to) return;
    setStages((prev) => {
      const card = (prev.find((c) => c.stage === from)?.cards || []).find((c) => c.job_id === job_id);
      return prev.map((col) => {
        if (col.stage === from) return { ...col, cards: col.cards.filter((c) => c.job_id !== job_id), count: col.count - 1 };
        if (col.stage === to && card) return { ...col, cards: [card, ...col.cards], count: col.count + 1 };
        return col;
      });
    });
    try { await ctx.req('POST', '/api/admin/desk/widgets/bays/move', { job_id, to_stage: to }); }
    catch (e) { ctx.showToast?.('Move failed: ' + (e?.message || '')); }
    load();
  };

  if (!stages) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-fitting-bay" style={{ overflowX: 'auto', display: 'flex', gap: 10, paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
      {stages.map((col) => (
        <div key={col.stage}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const p = dragRef.current; dragRef.current = null; if (p) move(p.job_id, p.from, col.stage); }}
          style={{ flex: '1 0 180px', minWidth: 180, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: '2px 4px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: BAY_STAGE_COLOR[col.stage] || SUB, flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--ff-caps)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: SUB, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label}</span>
            <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: TXT, background: 'rgba(255,255,255,0.06)', borderRadius: 999, padding: '0 7px', flexShrink: 0 }}>{col.count}</span>
          </div>
          {col.cards.map((card) => (
            <div key={card.job_id} draggable data-testid={`bay-card-${card.job_id}`}
              onDragStart={() => { dragRef.current = { job_id: card.job_id, from: col.stage }; }}
              onClick={() => { /* job detail — stubbed for now (FIX-02) */ }}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 10, padding: '9px 10px', marginBottom: 7, cursor: 'grab' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={buildIcon(card.vehicle)} alt="" style={{ width: 22, height: 22, flexShrink: 0, opacity: 0.9 }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, fontWeight: 700, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm ? ctx.dm(card.vehicle) : card.vehicle}</p>
                  <p style={{ margin: '1px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.dm ? ctx.dm(card.customer) : card.customer}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                {card.bay != null && card.bay !== '' && <span style={chip(SUB)}>Bay {card.bay}</span>}
                {card.parts_outstanding && <span style={chip('#F2B755')}>parts outstanding</span>}
              </div>
            </div>
          ))}
          {!col.cards.length && <p style={{ ...emptyP, textAlign: 'center', padding: '12px 0', opacity: 0.5 }}>—</p>}
        </div>
      ))}
    </div>
  );
};

// ── Gilliland World — Col's private space (FIX-02 Tile 3) ────────────────────
const GW_TABS = [['trips', 'Trips'], ['plans', 'Plans'], ['renos', 'Renos']];
const _daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr); if (isNaN(d.getTime())) return null;
  const ms = new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
};
const _countdown = (n) => {
  if (n == null) return null;
  if (n < 0) return { label: `${-n}d ago`, color: SUB };
  if (n === 0) return { label: 'today', color: '#EC7C24' };
  return { label: `${n}d`, color: n <= 7 ? '#EC7C24' : SUB };
};

export const GillilandWorldTile = ({ ctx }) => {
  const [tabs, setTabs] = useState(null);
  const [active, setActive] = useState('trips');
  const [val, setVal] = useState('');
  const load = useCallback(() => {
    ctx.req('GET', '/api/admin/desk/widgets/gilliland').then((d) => setTabs(d.tabs || {})).catch(() => {});
  }, [ctx]);
  useEffect(() => { load(); }, [load]);

  // Private — masked completely while presenting (same rule as the debrief).
  if (ctx.demo) {
    return (
      <div data-testid="widget-gilliland-masked" style={{ display: 'grid', placeItems: 'center', height: '100%', minHeight: 70 }}>
        <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, fontWeight: 700 }}>🔒 Gilliland World · hidden while presenting</p>
      </div>
    );
  }

  const add = async () => {
    const title = val.trim(); if (!title) return;
    setVal('');
    setTabs((t) => ({ ...t, [active]: [...(t[active] || []), { item_id: 'tmp-' + Date.now(), title, done: false }] }));
    try { await ctx.req('POST', '/api/admin/desk/widgets/gilliland', { tab: active, title }); } catch (e) { /* tile boundary */ }
    load();
  };
  const toggle = async (it) => {
    setTabs((t) => ({ ...t, [active]: (t[active] || []).map((x) => x.item_id === it.item_id ? { ...x, done: !x.done } : x) }));
    try { await ctx.req('PATCH', `/api/admin/desk/widgets/gilliland/${it.item_id}`, { done: !it.done }); } catch (e) { /* */ }
  };
  const del = async (it) => {
    setTabs((t) => ({ ...t, [active]: (t[active] || []).filter((x) => x.item_id !== it.item_id) }));
    try { await ctx.req('DELETE', `/api/admin/desk/widgets/gilliland/${it.item_id}`); } catch (e) { /* */ }
  };

  if (!tabs) return <p style={emptyP}>…</p>;
  const items = tabs[active] || [];
  const ph = active === 'trips' ? '+ Add a trip…' : active === 'plans' ? '+ Add a plan…' : '+ Add a reno…';
  return (
    <div data-testid="widget-gilliland" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {GW_TABS.map(([id, label]) => (
          <button key={id} data-testid={`gw-tab-${id}`} onClick={() => setActive(id)}
            style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '4px 12px', cursor: 'pointer',
              border: active === id ? '1px solid rgba(236,124,36,0.5)' : `1px solid ${BORDER}`,
              background: active === id ? 'rgba(236,124,36,0.14)' : 'transparent',
              color: active === id ? '#EC7C24' : SUB }}>{label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder={ph}
          style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 11px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: TXT, outline: 'none' }} />
        <button data-testid="gw-add" onClick={add} style={{ background: 'linear-gradient(135deg,#F79640,#EC7C24)', color: '#fff', border: 'none', borderRadius: 10, width: 34, flexShrink: 0, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>+</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {items.map((it) => {
          const cd = active === 'trips' ? _countdown(_daysUntil(it.date)) : null;
          return (
            <div key={it.item_id} data-testid={`gw-item-${it.item_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <button onClick={() => toggle(it)} title="done" style={{ width: 15, height: 15, flexShrink: 0, borderRadius: '50%', border: `1.5px solid ${it.done ? '#54D197' : SUB}`, background: it.done ? '#54D197' : 'transparent', cursor: 'pointer', padding: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: it.done ? SUB : TXT, textDecoration: it.done ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {it.title}{it.sub ? <span style={{ color: SUB, fontSize: 10.5 }}> · {it.sub}</span> : null}
              </span>
              {cd && <span style={chip(cd.color)}>{cd.label}</span>}
              <button onClick={() => del(it)} title="delete" style={{ flexShrink: 0, background: 'none', border: 'none', color: SUB, cursor: 'pointer', fontSize: 13, opacity: 0.6 }}>×</button>
            </div>
          );
        })}
        {!items.length && <p style={{ ...emptyP, padding: '14px 0', textAlign: 'center', opacity: 0.6 }}>Nothing here yet.</p>}
      </div>
    </div>
  );
};

export const QuotesAgingTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/quotes_aging');
  const H = { fresh: TEAL, warm: AMBER, hot: MAGENTA };
  const archive = async (q) => {
    if (!window.confirm(`Archive quote for ${q.business}? It'll disappear from Quotes-out + the money ticker (restore via Consulting Quotes → Archive drawer).`)) return;
    try { await ctx.req('POST', `/api/admin/cq/quotes/${q.quote_id}/archive`, {}); ctx.showToast('🗄 Quote archived'); ctx.load?.(); }
    catch (e) { ctx.showToast('Could not archive: ' + (e?.message || 'unknown')); }
  };
  if (!d) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-quotesaging" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6, alignContent: 'flex-start' }}>
      {d.quotes.map((q, k) => (
        <div key={q.quote_id || k} data-testid={`quote-row-${q.quote_id || k}`} style={{ position: 'relative', flex: '1 1 45%', minWidth: 120, padding: '7px 9px', borderRadius: 10, background: 'rgba(255,255,255,0.035)', border: `1px solid ${H[q.heat]}33`, boxShadow: q.heat === 'hot' ? `0 0 12px ${MAGENTA}22` : 'none' }}>
          <p style={{ ...rowP, fontWeight: 700, paddingRight: 18 }}>{maskIf(ctx.demo, q.business)}</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: AMBER }}>{money(ctx.demo, q.value)}</b>
            <span style={chip(H[q.heat])}>{Math.round(q.days)}d out</span>
          </div>
          {q.vision_engagement > 0 && <p style={{ margin: '3px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: TEAL, fontWeight: 800 }}>👀 explored the vision ×{q.vision_engagement}</p>}
          {q.quote_id && !ctx.demo && (
            <button data-testid={`quote-archive-${q.quote_id}`} onClick={() => archive(q)} title="Archive quote (restorable)"
              style={{ position: 'absolute', top: 4, right: 4, background: 'transparent', border: 'none', color: SUB, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '2px 5px', borderRadius: 6 }}
              onMouseEnter={(e) => { e.currentTarget.style.color = MAGENTA; e.currentTarget.style.background = 'rgba(236,124,36,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = SUB; e.currentTarget.style.background = 'transparent'; }}>🗄</button>
          )}
        </div>
      ))}
      {d.quotes.length === 0 && <p style={emptyP}>No open quotes.</p>}
    </div>
  );
};

export const JobsTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/jobs');
  const advance = async (jid) => {
    try { await ctx.req('POST', `/api/admin/cq/jobs/${jid}/advance`, {}); ctx.showToast('→ Stage advanced'); ctx.load(); }
    catch { ctx.showToast('Could not advance'); }
  };
  const archive = async (jid, biz) => {
    if (!window.confirm(`Archive job for ${biz}? It'll disappear from Active Jobs + the Desk widget (restore from the Archive drawer).`)) return;
    try { await ctx.req('POST', `/api/admin/cq/jobs/${jid}/archive`, {}); ctx.showToast('🗄 Job archived'); ctx.load(); }
    catch (e) { ctx.showToast('Could not archive: ' + (e?.message || 'unknown')); }
  };
  if (!d) return <p style={emptyP}>…</p>;
  return (
    <div data-testid="widget-jobs" className="studio-scroll" style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {d.jobs.map((j) => (
        <div key={j.job_id} data-testid={`job-row-${j.job_id}`} style={{ padding: '7px 9px', margin: '0 0 6px', borderRadius: 10, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(236,124,36,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <p style={{ ...rowP, fontWeight: 700 }}>{maskIf(ctx.demo, j.business_name)}</p>
            <b style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: AMBER, flexShrink: 0 }}>{money(ctx.demo, j.value)}</b>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 5 }}>
            {d.stages.map((s, i) => (
              <div key={s} title={d.labels[s]} style={{ flex: 1, height: 5, borderRadius: 3, background: i <= j.stage_index ? TEAL : 'rgba(255,255,255,0.09)' }} />
            ))}
            <span style={{ ...chip(TEAL), marginLeft: 4 }}>{d.labels[j.stage] || j.stage}</span>
            {j.stage !== 'invoiced' && (
              <button data-testid={`job-advance-${j.job_id}`} onClick={() => advance(j.job_id)} title="Advance stage"
                style={{ border: `1px solid ${TEAL}55`, background: 'transparent', color: TEAL, borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '0 7px', cursor: 'pointer', flexShrink: 0 }}>→</button>
            )}
            <button data-testid={`job-archive-${j.job_id}`} onClick={() => archive(j.job_id, j.business_name)} title="Archive job"
              style={{ border: '1px solid rgba(236,124,36,0.35)', background: 'transparent', color: '#EC7C24', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '0 7px', cursor: 'pointer', flexShrink: 0 }}>🗄</button>
          </div>
        </div>
      ))}
      {d.jobs.length === 0 && <p style={emptyP}>No jobs yet — they appear when a quote is accepted.</p>}
    </div>
  );
};

export const FocusTimerTile = ({ ctx }) => {
  const [workMin, setWorkMin] = useState(() => Number(localStorage.getItem('desk-pomo-work')) || 25);
  const [left, setLeft] = useState(workMin * 60);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState('work');
  const t = useRef(null);
  useEffect(() => {
    if (!running) return;
    t.current = setInterval(() => setLeft((s) => {
      if (s > 1) return s - 1;
      const nx = phase === 'work' ? 'break' : 'work';
      setPhase(nx);
      ctx.showToast(nx === 'break' ? '☕ Break — 5 minutes. You earned it.' : '🔨 Back to it — 25 on.');
      return (nx === 'work' ? workMin : 5) * 60;
    }), 1000);
    return () => clearInterval(t.current);
  }, [running, phase, workMin, ctx]);
  const total = (phase === 'work' ? workMin : 5) * 60;
  const pct = 1 - left / total;
  const mm = String(Math.floor(left / 60)).padStart(2, '0'), ss = String(left % 60).padStart(2, '0');
  const adj = (dv) => { const v = Math.max(5, Math.min(90, workMin + dv)); setWorkMin(v); localStorage.setItem('desk-pomo-work', v); if (!running && phase === 'work') setLeft(v * 60); };
  return (
    <div data-testid="widget-focustimer" className="no-drag" style={{ textAlign: 'center' }}>
      <div style={{ width: 86, height: 86, margin: '0 auto', borderRadius: '50%', display: 'grid', placeItems: 'center', background: `conic-gradient(${phase === 'work' ? TEAL : AMBER} ${pct * 360}deg, rgba(255,255,255,0.07) 0deg)` }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#17181B', display: 'grid', placeItems: 'center' }}>
          <span style={{ fontFamily: HEAD, fontSize: 18, fontWeight: 800, color: TXT, fontVariantNumeric: 'tabular-nums' }}>{mm}:{ss}</span>
        </div>
      </div>
      <p style={{ ...emptyP, margin: '5px 0', fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 800, color: phase === 'work' ? TEAL : AMBER }}>{phase === 'work' ? `focus · ${workMin}m` : 'break · 5m'}</p>
      <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
        <button data-testid="pomo-minus" onClick={() => adj(-5)} style={{ ...mini, padding: '2px 8px', fontSize: 10 }}>−5</button>
        <button data-testid="pomo-toggle" onClick={() => setRunning(!running)} style={{ ...mini, padding: '2px 12px', fontSize: 10, color: '#0E0F11', background: running ? AMBER : TEAL, border: 'none', fontWeight: 800 }}>{running ? '⏸' : '▶'}</button>
        <button data-testid="pomo-reset" onClick={() => { setRunning(false); setPhase('work'); setLeft(workMin * 60); }} style={{ ...mini, padding: '2px 8px', fontSize: 10 }}>↺</button>
        <button data-testid="pomo-plus" onClick={() => adj(5)} style={{ ...mini, padding: '2px 8px', fontSize: 10 }}>+5</button>
      </div>
    </div>
  );
};

export const QuickLinksTile = ({ ctx }) => {
  const [links, setLinks] = useState(() => { try { return JSON.parse(localStorage.getItem('desk-quicklinks')) || []; } catch { return []; } });
  const [name, setName] = useState(''); const [url, setUrl] = useState('');
  const save = (n) => { setLinks(n); localStorage.setItem('desk-quicklinks', JSON.stringify(n)); };
  const add = () => {
    const u = url.trim(); if (!u || !name.trim()) return;
    save([...links, { name: name.trim(), url: u.startsWith('http') ? u : `https://${u}` }]);
    setName(''); setUrl('');
  };
  return (
    <div data-testid="widget-quicklinks" className="no-drag">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {links.map((l, k) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: 'rgba(236,124,36,0.14)', border: `1px solid ${BORDER}` }}>
            <img src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(l.url)}&sz=16`} alt="" width="13" height="13" style={{ borderRadius: 3 }} />
            <a data-testid={`quicklink-${k}`} href={l.url} target="_blank" rel="noreferrer" style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700, color: TXT, textDecoration: 'none' }}>{l.name}</a>
            <button onClick={() => save(links.filter((_, i) => i !== k))} style={{ background: 'none', border: 'none', color: SUB, cursor: 'pointer', fontSize: 10, padding: 0 }}>✕</button>
          </span>
        ))}
        {links.length === 0 && <p style={emptyP}>Pin your go-tos — HubSpot, Reception.ai, anything.</p>}
      </div>
      <div style={{ display: 'flex', gap: 5 }}>
        <input data-testid="quicklink-name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '5px 9px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, outline: 'none' }} />
        <input data-testid="quicklink-url" placeholder="URL" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} style={{ flex: 1.4, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '5px 9px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, outline: 'none' }} />
        <button data-testid="quicklink-add" onClick={add} style={{ ...mini, padding: '2px 10px', fontSize: 10, color: TEAL, borderColor: 'rgba(84,209,151,0.4)' }}>＋</button>
      </div>
    </div>
  );
};

const WMOJI = (c) => c <= 1 ? '☀️' : c <= 3 ? '⛅' : c <= 48 ? '🌫' : c <= 67 ? '🌧' : c <= 77 ? '🌨' : c <= 82 ? '🌧' : '⛈';
export const WeatherTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/weather', 600000);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.configured) return <p style={emptyP}>{d.reason}</p>;
  return (
    <div data-testid="widget-weather" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 30 }}>{WMOJI(d.code || 0)}</span>
      <div>
        <p style={{ margin: 0, fontFamily: HEAD, fontSize: 22, fontWeight: 800, color: TXT }}>{Math.round(d.temp)}°<span style={{ fontSize: 11, color: SUB, fontFamily: 'Source Sans 3, sans-serif' }}> Brisbane</span></p>
        <p style={{ ...emptyP, fontSize: 10 }}>{Math.round(d.lo)}–{Math.round(d.hi)}° · 💧{d.rain ?? 0}% · 💨{Math.round(d.wind)}km/h</p>
      </div>
    </div>
  );
};

export const WonChartTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/won_by_month', 300000);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.months.length) return <p style={emptyP}>No payments yet — bars grow as money lands.</p>;
  const max = Math.max(...d.months.map((m) => m.value), 1);
  return (
    <div data-testid="widget-wonchart" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: '100%', minHeight: 0, paddingTop: 4 }}>
      {d.months.map((m) => (
        <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', textAlign: 'center' }}>
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, color: AMBER }}>{money(ctx.demo, m.value)}</span>
          <div style={{ height: `${Math.max(6, (m.value / max) * 78)}%`, borderRadius: '6px 6px 2px 2px', background: `linear-gradient(180deg, ${TEAL}, ${VIOLET})`, marginTop: 3 }} />
          <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, color: SUB, marginTop: 3 }}>{m.month.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

export const RepliesTrendTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/widgets/replies_trend', 300000);
  if (!d) return <p style={emptyP}>…</p>;
  const vals = d.weeks.map((w) => w.count);
  const max = Math.max(...vals, 1);
  const W = 100, H = 34;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${H - (v / max) * (H - 4) - 2}`).join(' ');
  return (
    <div data-testid="widget-repliestrend">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 52, display: 'block' }}>
        <defs>
          <linearGradient id="rt-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={TEAL} stopOpacity="0.35" /><stop offset="100%" stopColor={TEAL} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#rt-fill)" />
        <polyline points={pts} fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, color: SUB }}>{d.weeks[0]?.week.slice(5)}</span>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, color: TEAL }}>{vals.reduce((a, b) => a + b, 0)} replies · 8 wks</span>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, color: SUB }}>{d.weeks[d.weeks.length - 1]?.week.slice(5)}</span>
      </div>
    </div>
  );
};


// ── PROJECTS: by-stage summary + "needs attention" nudge. Manual stages,
//    no live Emergent polling (no public API). Deep link to /admin/projects.
const STAGE_C = { idea: SUB, active: TEAL, paused: AMBER, delivered: '#7BB0EC', maintained: VIOLET };
export const ProjectsTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/projects/summary/desk');
  if (!d) return <p style={emptyP}>…</p>;
  const total = Object.values(d.by_stage || {}).reduce((a, b) => a + b, 0);
  if (!total) {
    return (
      <div data-testid="projects-tile-empty" style={{ textAlign: 'center', padding: '18px 8px' }}>
        <p style={{ margin: 0, fontSize: 22, opacity: 0.5 }}>◈</p>
        <p style={{ margin: '8px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, lineHeight: 1.55 }}>No projects yet.<br/><a href="/admin/projects" style={{ color: TEAL, textDecoration: 'none', fontWeight: 700 }}>+ New project →</a></p>
      </div>
    );
  }
  return (
    <div data-testid="projects-tile" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {Object.entries(d.by_stage || {}).filter(([, n]) => n > 0).map(([s, n]) => (
          <a key={s} href="/admin/projects" data-testid={`projects-stage-${s}`}
            style={{ display: 'inline-flex', gap: 5, alignItems: 'center', padding: '3px 9px', borderRadius: 999, background: `${STAGE_C[s]}18`, border: `1px solid ${STAGE_C[s]}44`, textDecoration: 'none', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 700, color: STAGE_C[s] }}>
            <b style={{ fontSize: 12 }}>{n}</b> {s}
          </a>
        ))}
      </div>
      {(d.needs_attention || []).length > 0 && (
        <div style={{ padding: '8px 10px', marginBottom: 8, borderRadius: 10, background: 'rgba(247,150,64,0.09)', border: '1px solid rgba(247,150,64,0.28)' }}>
          <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: AMBER }}>⚠ Needs attention</p>
          {(d.needs_attention || []).slice(0, 4).map((p) => (
            <p key={p.project_id} style={{ margin: '4px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT }}>· {p.name}{p.client_tag ? <span style={{ color: SUB }}> · {p.client_tag}</span> : null}<span style={{ color: p.reason === 'paused' ? AMBER : MAGENTA, fontWeight: 700, marginLeft: 5 }}>{p.reason === 'paused' ? '(paused)' : `(idle ${p.days_idle}d)`}</span></p>
          ))}
        </div>
      )}
      <a href="/admin/projects" data-testid="projects-tile-open" style={{ marginTop: 'auto', textAlign: 'center', padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', color: SUB, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 700, textDecoration: 'none' }}>Open all projects →</a>
    </div>
  );
};

// ── LLM BUDGET tile — honest account-level number. Not per-project.
export const LLMBudgetTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/projects/llm_budget/current', 300000);
  if (!d) return <p style={emptyP}>…</p>;
  if (!d.available) {
    return <p style={{ ...emptyP, fontSize: 10.5 }}>LLM budget: {d.reason || 'unavailable'}</p>;
  }
  const pct = d.pct_used ?? 0;
  const barCol = pct >= 90 ? MAGENTA : pct >= 70 ? AMBER : TEAL;
  return (
    <div data-testid="llm-budget-tile" style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: SUB }}>TravelTrucks OS · AI budget</p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <b style={{ fontFamily: HEAD, fontSize: 22, color: TXT }}>${d.spend_usd.toFixed(2)}</b>
        <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB }}>of ${d.budget_usd.toFixed(2)}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: barCol, transition: 'width 0.4s' }} />
      </div>
      <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, lineHeight: 1.5 }}>
        Account-level (across all AI features). Emergent doesn&apos;t expose per-project usage — this is the honest total.
      </p>
    </div>
  );
};

// ── SWEEP AUDIT tile — every automated sweep (orphan, follow-up, expiry)
//    logs a row to db.sweep_runs. This tile surfaces the last few so MJ
//    can see what the background touched and — crucially — what it
//    wisely skipped because of user-restore pins. "User action beats
//    sweeps" only earns MJ's trust if MJ can SEE it happening.
const SWEEP_KIND_META = {
  orphan: { icon: '🧹', label: 'Orphan', c: TEAL },
  followup: { icon: '📨', label: 'Follow-up', c: AMBER },
};
const sweepAgo = (ts) => {
  if (!ts) return '';
  const m = (Date.now() - new Date(ts).getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)}m ago`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
export const SweepAuditTile = ({ ctx }) => {
  const d = useW(ctx.req, '/api/admin/desk/sweep_runs?limit=12', 120000);
  if (!d) return <p style={emptyP}>…</p>;
  const runs = d.runs || [];
  if (runs.length === 0) {
    return (
      <div data-testid="sweep-audit-empty" style={{ textAlign: 'center', padding: '16px 6px' }}>
        <p style={{ margin: 0, fontSize: 20 }}>🧹</p>
        <p style={{ ...emptyP, marginTop: 6 }}>No sweep runs yet — orphan & follow-up sweeps log here every hour.</p>
      </div>
    );
  }
  return (
    <div data-testid="sweep-audit-tile" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
        <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>Sweep audit · last {runs.length}</p>
      </div>
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {runs.map((r, k) => {
          const meta = SWEEP_KIND_META[r.kind] || { icon: '⚙', label: r.kind, c: SUB };
          const touched = (r.quotes_archived || 0) + (r.jobs_archived || 0) + (r.touched || 0);
          const pinned = r.user_pinned_skipped || 0;
          return (
            <div key={k} data-testid={`sweep-row-${k}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '5px 7px', margin: '2px 0', borderRadius: 8, background: touched ? 'rgba(255,255,255,0.035)' : 'transparent', borderLeft: `2px solid ${meta.c}66` }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{meta.icon}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: TXT, fontWeight: touched ? 700 : 500 }}>
                  {meta.label} · {sweepAgo(r.ran_at)}
                </p>
                <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, lineHeight: 1.4 }}>
                  {touched === 0 && !pinned ? 'clean · nothing to touch' :
                    <>
                      {touched > 0 && <span style={{ color: meta.c, fontWeight: 700 }}>{touched} archived</span>}
                      {touched > 0 && pinned > 0 && ' · '}
                      {pinned > 0 && <span style={{ color: TEAL, fontWeight: 700 }}>{pinned} skipped user-pinned ✓</span>}
                      {r.expired > 0 && <> · <span style={{ color: MAGENTA, fontWeight: 700 }}>{r.expired} expired</span></>}
                      {(r.nudge1 + r.nudge2) > 0 && <> · {r.nudge1 + r.nudge2} nudged</>}
                    </>
                  }
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, letterSpacing: '0.05em', textAlign: 'center' }}>
        User action beats sweeps · pins respected
      </p>
    </div>
  );
};
